import { Decimal } from "decimal.js-light";
import { isValidDecimalString, normalizeDecimalString } from "../formatter/formatterUtils";
import { DISPLAY_SCALE, INPUT_SCALE } from "./scales";

Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
});

export type DecimalInput = string | number | null | undefined;

type DecimalFormatOptions = {
  scale?: number;
};

export class DecimalUtils {
  static readonly INPUT_SCALE = INPUT_SCALE;
  static readonly DISPLAY_SCALE = DISPLAY_SCALE;

  private static toDecimal(value: DecimalInput): Decimal {
    if (value === null || value === undefined || value === "") return new Decimal(0);

    const normalized = normalizeDecimalString(String(value));
    if (!isValidDecimalString(normalized)) return new Decimal(0);

    return new Decimal(normalized);
  }

  private static format(value: Decimal, options?: DecimalFormatOptions): string {
    const { scale = this.INPUT_SCALE } = options ?? {};
    return value.toFixed(scale);
  }

  static toString(value: DecimalInput, options?: DecimalFormatOptions): string {
    const { scale = this.DISPLAY_SCALE } = options ?? {};
    return this.format(this.toDecimal(value), { scale });
  }

  static toNumber(value: DecimalInput, options?: DecimalFormatOptions): number {
    const { scale = this.INPUT_SCALE } = options ?? {};
    return Number(this.toString(value, { scale }));
  }

  static add(values: DecimalInput[], options?: DecimalFormatOptions): string {
    const total = values.reduce((runningTotal, currentValue) => {
      return runningTotal.plus(this.toDecimal(currentValue));
    }, new Decimal(0));

    return this.format(total, options);
  }

  static multiply(valueA: DecimalInput, valueB: DecimalInput, options?: DecimalFormatOptions): string {
    const result = this.toDecimal(valueA).mul(this.toDecimal(valueB));

    return this.format(result, options);
  }

  static divide(valueA: DecimalInput, valueB: DecimalInput, options?: DecimalFormatOptions): string {
    const divisor = this.toDecimal(valueB);
    if (divisor.isZero()) return this.format(new Decimal(0), options);

    const result = this.toDecimal(valueA).div(divisor);

    return this.format(result, options);
  }

  static equals(valueA: DecimalInput, valueB: DecimalInput, options?: DecimalFormatOptions): boolean {
    const { scale = this.INPUT_SCALE } = options ?? {};
    return this.format(this.toDecimal(valueA), { scale }) === this.format(this.toDecimal(valueB), { scale });
  }
}
